import { useCallback, useState } from 'react';

/**
 * The Project Plan's schedule view switches — `{ critical_path, links }` — per user across every
 * plan, persisted on the server (module `project_plan_schedule_view`).
 *
 * Every write posts the whole merged object, because the server replaces the stored value: a
 * write of one key alone would drop the others.
 */
const PREFERENCE_MODULE = 'project_plan_schedule_view';

const DEFAULTS = { critical_path: false, links: false };

/** Only boolean entries survive: a malformed stored value falls back to the defaults key by key. */
const normalise = (candidate) => {
    const value = { ...DEFAULTS };

    if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
        Object.entries(candidate).forEach(([key, entry]) => {
            if (typeof entry === 'boolean') {
                value[key] = entry;
            }
        });
    }

    return value;
};

/**
 * Fire-and-forget: flipping a switch never waits on the round trip. A failure costs nothing for the
 * rest of the session; the next full page load reads the server value back.
 */
async function persistView(value) {
    try {
        const response = await fetch('/preferences', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content ?? '',
                Accept: 'application/json',
            },
            body: JSON.stringify({ module: PREFERENCE_MODULE, value }),
        });

        // `fetch` resolves on an expired session (419) or a login redirect, so check the status.
        if (!response.ok) {
            console.warn('[useProjectPlanScheduleView] The schedule view was not saved', response.status);
        }
    } catch (e) {
        console.warn('[useProjectPlanScheduleView] Failed to persist the schedule view', e);
    }
}

/**
 * Returns `[view, setViewKey(key, value)]`.
 */
export default function useProjectPlanScheduleView() {
    const [view, setView] = useState(() => normalise(window.projectPlanScheduleViewPreference));

    const setViewKey = useCallback((key, value) => {
        setView((prev) => {
            // The page's copy wins over this mount's state, so a key another mount of this hook
            // wrote since this one loaded is kept.
            const next = { ...prev, ...normalise(window.projectPlanScheduleViewPreference), [key]: value };

            // The critical path needs the links: turning it on turns Links on, and turning Links
            // off turns it off.
            if (key === 'critical_path' && value) {
                next.links = true;
            }

            if (key === 'links' && !value) {
                next.critical_path = false;
            }

            // The plan unmounts whenever the Project Management tabs switch away from it, and the
            // next mount reads this copy first, so it has to move with the state.
            window.projectPlanScheduleViewPreference = next;

            persistView(next);

            return next;
        });
    }, []);

    return [view, setViewKey];
}
